import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  RangeCalendar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tooltip,
} from '@heroui/react';
import {
  FilterSelect,
  FilterOption,
  ModelSearchSelect,
  ResponsiveTooltip,
} from '../../../../components/ui';
import {
  SearchInputIc,
  AccountIcon,
  CalendarFilterIc,
  ResetFiltersIc,
} from '../../../../components/icons';
import { TransactionsFilters, TransactionsFiltersProps } from '../types';
import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
import { useTranslations } from '../../../../contexts/translation.context';

interface FiltersSectionProps extends TransactionsFiltersProps {
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const FiltersSection: React.FC<FiltersSectionProps> = ({
  filters,
  localSearch,
  localId,
  localCompanyId,
  availableModels,
  modelsLoading,
  availableAccounts,
  accountsLoading,
  onSearchChange,
  onIdChange,
  onCompanyIdChange,
  onFilterChange,
  onModelSearchChange,
  onAccountSearchChange,
  onRefresh,
  isRefreshing = false,
  hasActiveFilters = false,
  onResetFilters,
}) => {
  const { t } = useTranslations();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<any>(null);

  const formatDateRange = (dateRange: any) => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return t('transactions.filters.date');
    }

    const startDate = dateRange.start.toDate(getLocalTimeZone());
    const endDate = dateRange.end.toDate(getLocalTimeZone());

    return `${startDate.toLocaleDateString('ru-RU')} - ${endDate.toLocaleDateString('ru-RU')}`;
  };

  const handleDateRangeChange = (range: any) => {
    setSelectedDateRange(range);

    const formatDateToString = (dateValue: any) => {
      const localDate = dateValue.toDate(getLocalTimeZone());
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (range?.start && range?.end) {
      const startDateString = formatDateToString(range.start);
      const endDateString = formatDateToString(range.end);
      onFilterChange('createdAtStart', startDateString);
      onFilterChange('createdAtEnd', endDateString);
      setIsCalendarOpen(false);
    } else {
      onFilterChange('createdAtStart', null);
      onFilterChange('createdAtEnd', null);
    }
  };

  // Синхронизируем состояние календаря с фильтрами
  useEffect(() => {
    if (filters.createdAtStart && filters.createdAtEnd) {
      setSelectedDateRange({
        start: parseDate(filters.createdAtStart),
        end: parseDate(filters.createdAtEnd),
      });
    } else {
      setSelectedDateRange(null);
    }
  }, [filters.createdAtStart, filters.createdAtEnd]);
  return (
    <div className="px-4 pb-6 md:px-6 md:pb-6 flex items-center gap-4 justify-between w-full">
      <div className="flex items-center gap-4 md:justify-start max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        {/* <Input
          type="text"
          placeholder="Имя/ID"
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: "w-[17.0625rem]",
            base: "w-[17.0625rem]",
            input: "text-sm",
            inputWrapper: "rounded-lg h-9 w-[17.0625rem]",
          }}
        /> */}

        <Input
          type="text"
          placeholder={'ID'}
          size="sm"
          value={localId}
          onChange={(e) => onIdChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm',
            inputWrapper: 'rounded-lg h-9 w-[17.0625rem]',
          }}
        />
        <Input
          type="text"
          placeholder={t('transactions.filters.campaign')}
          size="sm"
          value={localCompanyId}
          onChange={(e) => onCompanyIdChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm',
            inputWrapper: 'rounded-lg h-9 w-[17.0625rem]',
          }}
        />
        <Popover isOpen={isCalendarOpen} onOpenChange={setIsCalendarOpen} placement="bottom-start">
          <PopoverTrigger>
            {/* <Button
              variant="bordered"
              className="h-9 rounded-lg border-tin bg-white text-auth-muted justify-start px-3 min-w-[12.5rem]"
              startContent={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                  <path fill="none" stroke="#A8A8B0" strokeLinecap="round" strokeWidth="1.5" d="M8 2v3m8-3v3M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"/>
                </svg>
              }
            >
              {formatDate(selectedDate)}
            </Button> */}
            <Button
              startContent={<CalendarFilterIc />}
              className={`bg-auth-surface border-none ${selectedDateRange ? '!text-auth-ink' : '!text-auth-muted'} rounded-2 !px-3 !min-h-9 !h-9 w-fit max-w-[20rem] justify-start`}
            >
              {formatDateRange(selectedDateRange)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <RangeCalendar
              value={selectedDateRange}
              onChange={handleDateRangeChange}
              minValue={parseDate('1900-01-01')}
              maxValue={today(getLocalTimeZone())}
              className="w-auto"
            />
          </PopoverContent>
        </Popover>

        <ModelSearchSelect
          placeholder={t('transactions.filters.account')}
          options={[
            ...availableModels.map(
              (model) =>
                ({
                  key: model.id,
                  avatar: model.avatar,
                  username: model.username,
                  name: model.name,
                  label: `${model.name} (@${model.username})`,
                }) as FilterOption
            ),
          ]}
          selectedKey={filters.models.length > 0 ? filters.models[0] : 'all'}
          onSelectionChange={(value) => {
            const newModels = value === 'all' ? [] : [value];
            onFilterChange('models', newModels);
          }}
          onSearchChange={onModelSearchChange}
          isLoading={modelsLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />
        <FilterSelect
          placeholder={t('transactions.filters.status')}
          options={[
            { key: 'all', label: t('transactions.filters.allStatuses') },
            { key: 'done', label: t('transactions.filters.statusConfirmed') },
            { key: 'loading', label: t('transactions.filters.statusPending') },
            { key: 'undo', label: t('transactions.filters.statusReturned') },
          ]}
          selectedKey={filters.status || 'all'}
          variant="campaign"
          onSelectionChange={(value) => {
            const statusValue = value === 'all' ? null : value;
            onFilterChange('status', statusValue);
          }}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="10" cy="10" r="7.5" stroke="#A8A8B0" strokeWidth="1.5" />
              <path
                d="M10 6.25V10L12.5 12.5"
                stroke="#A8A8B0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          autoWidth={true}
          popoverWidth={320}
        />
        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && onResetFilters && (
          <Tooltip content={t('transactions.resetFilters')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default FiltersSection;
